/**
 * DV-DEP-001: Production List Component
 *
 * Component that displays finished productions in a grid/table format
 * with download, upload, and scheduling actions.
 */

import React, { useState } from 'react';
import {
  Download,
  Upload,
  Calendar,
  Clock,
  Film,
  AlertCircle,
  CheckCircle,
  Loader2,
  Eye,
  ExternalLink,
  Youtube,
  Copy
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatTimeAgo } from '@/utils/format';
import { useDownloadProduction } from '@/hooks/useDeployments';
import type { Production } from '@/types/deployments';

interface ProductionListProps {
  productions: Production[];
  isLoading: boolean;
  onUploadNow: (production: Production) => void;
  onScheduleUpload: (production: Production) => void;
  onViewJobs?: (production: Production) => void;
}

const ProductionList: React.FC<ProductionListProps> = ({
  productions,
  isLoading,
  onUploadNow,
  onScheduleUpload,
  onViewJobs,
}) => {
  const downloadProduction = useDownloadProduction();
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const handleDownload = async (production: Production) => {
    try {
      setDownloadingIds(prev => new Set(prev.add(production.id)));
      await downloadProduction.mutateAsync({
        productionId: production.id,
        filename: `${production.title || `production-${production.id}`}.mp4`,
      });
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(production.id);
        return newSet;
      });
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      // You could add a toast notification here
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown';

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (mins === 0) {
      return `${secs}s`;
    }

    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAspectRatioDisplay = (aspectRatio: Production['aspectRatio']): string => {
    switch (aspectRatio) {
      case '16:9':
        return 'Landscape';
      case '9:16':
        return 'Portrait';
      case '1:1':
        return 'Square';
      default:
        return aspectRatio;
    }
  };

  const getShortHint = (production: Production): string | undefined => {
    if (production.aspectRatio === '9:16' && production.duration && production.duration <= 60) {
      return 'Short';
    }
    return undefined;
  };

  const getDeploymentStatusBadge = (production: Production & { latestDeployment?: any }) => {
    const deployment = production.latestDeployment;

    if (!deployment) {
      return (
        <div className="flex items-center space-x-1 text-white/60 text-xs font-mono">
          <CheckCircle className="w-3 h-3" />
          <span>Ready</span>
        </div>
      );
    }

    switch (deployment.status) {
      case 'published':
        return (
          <div className="flex items-center space-x-1 text-green-400 text-xs font-mono">
            <CheckCircle className="w-3 h-3" />
            <span>Published</span>
          </div>
        );
      case 'uploading':
        return (
          <div className="flex items-center space-x-1 text-blue-400 text-xs font-mono">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Uploading</span>
          </div>
        );
      case 'processing':
        return (
          <div className="flex items-center space-x-1 text-yellow-400 text-xs font-mono">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Processing</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex items-center space-x-1 text-red-400 text-xs font-mono">
            <AlertCircle className="w-3 h-3" />
            <span>Failed</span>
          </div>
        );
      case 'scheduled':
        return (
          <div className="flex items-center space-x-1 text-purple-400 text-xs font-mono">
            <Clock className="w-3 h-3" />
            <span>Scheduled</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-1 text-white/60 text-xs font-mono">
            <CheckCircle className="w-3 h-3" />
            <span>Ready</span>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-black/60 rounded-lg p-4 animate-pulse">
              <div className="h-40 bg-white/10 rounded-lg mb-4" />
              <div className="h-4 bg-white/10 rounded mb-2" />
              <div className="h-3 bg-white/10 rounded w-2/3 mb-4" />
              <div className="flex space-x-2">
                <div className="h-8 bg-white/10 rounded flex-1" />
                <div className="h-8 bg-white/10 rounded flex-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (productions.length === 0) {
    return (
      <div className="bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-12 text-center">
        <Film className="w-16 h-16 text-white/20 mx-auto mb-4" />
        <h3 className="text-xl text-white/60 mb-2">No finished productions</h3>
        <p className="text-white/40 text-sm mb-6">
          Completed video productions will appear here when ready for deployment.
        </p>
        <a
          href="/dashboard/executions"
          className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors font-mono text-sm"
        >
          <span>View Executions</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    );
  }

  return (
    <div className="bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {productions.map((production) => {
          const shortHint = getShortHint(production);
          const isDownloading = downloadingIds.has(production.id);

          return (
            <div
              key={production.id}
              className="bg-black/60 border border-white/10 rounded-lg overflow-hidden hover:border-cyan-500/30 transition-all duration-300 group"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video bg-black/80 flex items-center justify-center border-b border-white/10">
                {production.thumbnailUrl ? (
                  <img
                    src={production.thumbnailUrl}
                    alt={production.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-white/40">
                    <Film className="w-8 h-8" />
                  </div>
                )}

                {/* Aspect Ratio Badge */}
                <div className="absolute top-2 left-2">
                  <span className={cn(
                    'inline-flex items-center px-2 py-1 rounded text-xs font-mono',
                    'bg-black/60 backdrop-blur-sm border',
                    production.aspectRatio === '9:16'
                      ? 'border-purple-500/30 text-purple-400'
                      : production.aspectRatio === '1:1'
                      ? 'border-blue-500/30 text-blue-400'
                      : 'border-green-500/30 text-green-400'
                  )}>
                    {getAspectRatioDisplay(production.aspectRatio)}
                  </span>
                </div>

                {/* Short Hint */}
                {shortHint && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-yellow-500/20 border border-yellow-500/30 text-yellow-400">
                      {shortHint}
                    </span>
                  </div>
                )}

                {/* Duration */}
                {production.duration && (
                  <div className="absolute bottom-2 right-2">
                    <span className="inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-mono bg-black/60 backdrop-blur-sm border border-white/20 text-white">
                      <Clock className="w-3 h-3" />
                      <span>{formatDuration(production.duration)}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Title and Description */}
                <div className="mb-3">
                  <h3 className="text-white font-medium text-sm mb-1 line-clamp-2 group-hover:text-cyan-400 transition-colors">
                    {production.title || `Production ${production.id}`}
                  </h3>
                  {production.description && (
                    <p className="text-white/60 text-xs line-clamp-2">
                      {production.description}
                    </p>
                  )}
                </div>

                {/* Metadata */}
                <div className="flex items-center justify-between text-xs text-white/40 font-mono mb-4">
                  <span>{formatTimeAgo(production.createdAt)}</span>
                  {getDeploymentStatusBadge(production)}
                </div>

                {/* Actions */}
                <div className="grid grid-cols-4 gap-2">
                  {/* Download */}
                  <button
                    onClick={() => handleDownload(production)}
                    disabled={isDownloading}
                    className={cn(
                      'px-3 py-2 border rounded-lg text-xs font-mono transition-all',
                      'flex items-center justify-center space-x-1',
                      isDownloading
                        ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
                        : 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30 hover:scale-105'
                    )}
                    title="Download video file"
                  >
                    {isDownloading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3" />
                    )}
                    <span className="hidden sm:inline">Download</span>
                  </button>

                  {/* Upload Now */}
                  <button
                    onClick={() => onUploadNow(production)}
                    className="px-3 py-2 bg-green-500/20 border border-green-500/30 text-green-400 hover:bg-green-500/30 hover:scale-105 rounded-lg text-xs font-mono transition-all flex items-center justify-center space-x-1"
                    title="Upload to YouTube now"
                  >
                    <Upload className="w-3 h-3" />
                    <span className="hidden sm:inline">Upload</span>
                  </button>

                  {/* Schedule */}
                  <button
                    onClick={() => onScheduleUpload(production)}
                    className="px-3 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 hover:bg-purple-500/30 hover:scale-105 rounded-lg text-xs font-mono transition-all flex items-center justify-center space-x-1"
                    title="Schedule for later"
                  >
                    <Calendar className="w-3 h-3" />
                    <span className="hidden sm:inline">Schedule</span>
                  </button>

                  {/* View Jobs */}
                  {onViewJobs && (
                    <button
                      onClick={() => onViewJobs(production)}
                      className="px-3 py-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 hover:bg-orange-500/30 hover:scale-105 rounded-lg text-xs font-mono transition-all flex items-center justify-center space-x-1"
                      title="View deployment jobs"
                    >
                      <Eye className="w-3 h-3" />
                      <span className="hidden sm:inline">Jobs</span>
                    </button>
                  )}
                </div>

                {/* Published Link / Project/Execution Link */}
                {((production as any).latestDeployment?.platformUrl || production.projectId || production.executionId) && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="space-y-2 text-xs">
                      {/* Published Link */}
                      {(production as any).latestDeployment?.platformUrl && (production as any).latestDeployment?.status === 'published' && (
                        <div className="flex items-center justify-between">
                          <a
                            href={(production as any).latestDeployment.platformUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-green-400/80 hover:text-green-400 transition-colors"
                          >
                            <Youtube className="w-3 h-3" />
                            <span>View on YouTube</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                          <button
                            onClick={() => handleCopyLink((production as any).latestDeployment.platformUrl)}
                            className="flex items-center space-x-1 text-white/40 hover:text-white/60 transition-colors"
                            title="Copy link"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      )}

                      {/* Execution Link */}
                      {production.executionId && (
                        <a
                          href={`/dashboard/execution/${production.executionId}`}
                          className="flex items-center space-x-1 text-cyan-400/60 hover:text-cyan-400 transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          <span>View Execution</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductionList;